import { useState } from 'react'

import { useUnsafeCallback, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  CostCentersNestedListDocument,
  CostCentersNestedListQuery,
  CostCentersValueObjectsCostCenterStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlChildrenDataCollection } from 'modules/core/hooks/useGraphqlChildrenDataCollection'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { CostCenterEditModal } from 'modules/cost_centers/components/CostCenterNestedList/Edit'
import {
  COST_CENTER_STATUS,
  COST_CENTER_STRUCTURE,
  COST_CENTER_STRUCTURE_TYPES,
} from 'modules/cost_centers/lib/consts'
import {
  CostCenterConfigurations,
  CostCenterNestedListDetails,
  StructuredCostCenter,
} from 'modules/cost_centers/lib/types'

const getCostCenterStatus = (status: string) => {
  if (status === COST_CENTER_STATUS.active) return 'positive'
  return 'warning' // Or 'critical'
}

const getCostCenterStatusLabel = (status: CostCentersValueObjectsCostCenterStatusEnum) => {
  if (status === COST_CENTER_STATUS.active)
    return i18n.t('finance.cost_centers_list.filters.status.active')
  if (status === COST_CENTER_STATUS.inactive)
    return i18n.t('finance.cost_centers_list.filters.status.inactive')
  return '-'
}

const CostCentersNestedList = ({
  canManageCostCenters,
  configurations,
}: {
  canManageCostCenters: boolean
  configurations: CostCenterConfigurations
}) => {
  const { company } = useCurrentSession()
  const [costCenterToEdit, setCostCenterToEdit] = useState<StructuredCostCenter | null>(null)

  const configurationJobCatalogStructure = useUnsafeMemo(() => {
    return configurations.find(({ structure }) =>
      [
        COST_CENTER_STRUCTURE.job_catalog_family,
        COST_CENTER_STRUCTURE.job_catalog_function,
        COST_CENTER_STRUCTURE.job_catalog_role,
      ].includes(structure)
    )
  }, [configurations])

  const hideFilters = useUnsafeMemo(() => !configurations?.length, [configurations])

  const handleEditAsset = useUnsafeCallback((item: CostCenterNestedListDetails) => {
    setCostCenterToEdit(item)
  }, [])

  const { fetchChildren: fetchCostCenterDetails } = useGraphqlChildrenDataCollection({
    query: CostCentersNestedListDocument,
    perPage: 5,
    fetchPolicy: 'network-only',
    mapVariables: ({ item }: { item: CostCenterNestedListDetails }) => ({
      ancestorUuids: item.jobCatalogNodeUuid ? [item.jobCatalogNodeUuid] : undefined,
      legalEntityIds: item.legalEntity?.id ? [item.legalEntity?.id] : undefined,
      statuses: item.status ? [item.status] : undefined,
      structureType: COST_CENTER_STRUCTURE_TYPES.job_catalog,
    }),
    mapData: ({ data }: { data: CostCentersNestedListQuery }) =>
      data.costCenters.structuredCostCentersConnection.edges.map((edge) => edge.node),
    totalCount: (data: CostCentersNestedListQuery) =>
      data.costCenters.structuredCostCentersConnection.totalCount ?? 0,
  })

  const mapData = useUnsafeCallback((data: CostCentersNestedListQuery) => {
    return data.costCenters.structuredCostCentersConnection.edges.map((edge) => edge.node)
  }, [])

  const dataSourceOptions = useUnsafeMemo(
    () => ({
      document: CostCentersNestedListDocument,
      mapData: (tableData: CostCentersNestedListQuery) => mapData(tableData),
      sortings: {
        name: {
          label: i18n.t('finance.account_list.headers.name'),
          sortBy: 'name',
        },
      },
      defaultSortings: {
        field: 'name' as const,
        order: 'asc' as const,
      },
      search: {
        enabled: !hideFilters,
      },
      ...(hideFilters
        ? {}
        : {
            filters: {
              legalEntityIds: {
                type: 'in' as const,
                label: i18n.t('finance.cost_centers_list.filters.legal_entity'),
                options: {
                  options: company.legalEntities.map(({ id, legalName }) => ({
                    value: id,
                    label: legalName || '',
                  })),
                },
              },
              statuses: {
                type: 'in' as const,
                label: i18n.t('finance.cost_centers_list.filters.status.label'),
                options: {
                  options: [
                    {
                      value: 'active' as const,
                      label: i18n.t('finance.cost_centers_list.filters.status.active'),
                    },
                    {
                      value: 'inactive' as const,
                      label: i18n.t('finance.cost_centers_list.filters.status.inactive'),
                    },
                  ],
                },
              },
            },
          }),
      ...(hideFilters
        ? {}
        : {
            presets: [
              {
                label: i18n.t('finance.cost_centers_list.filters.status.active'),
                filter: { statuses: ['active' as const] },
              },
              {
                label: i18n.t('finance.cost_centers_list.filters.status.inactive'),
                filter: { statuses: ['inactive' as const] },
              },
            ],
          }),
      itemActions: (item: CostCenterNestedListDetails) => {
        const actions = []

        if (canManageCostCenters && item.structure === null) {
          actions.push({
            label: i18n.t('finance.cost_centers_list.popover.edit'),
            icon: Pencil,
            onClick: () => {
              handleEditAsset(item)
            },
            critical: false,
          })
        }

        return actions
      },
      itemsWithChildren: (item: CostCenterNestedListDetails) => {
        const structure = item.structure

        if (!structure) return false

        return structure !== configurationJobCatalogStructure?.structure
      },
      fetchChildren: fetchCostCenterDetails,
      mapVariables: ({
        search,
        filters,
        sortings,
      }: {
        search?: string
        sortings?: Array<{ field: string; order: string }>
        filters?: {
          legalEntityIds?: number[]
          statuses?: CostCentersValueObjectsCostCenterStatusEnum[]
        }
      }) => {
        return {
          search: search,
          companyId: company.id,
          sortOrder: sortings?.[0],
          statuses: filters?.statuses,
          legalEntityIds: filters?.legalEntityIds,
          structureType: COST_CENTER_STRUCTURE_TYPES.job_catalog,
        }
      },
      mapPagination: (tableData: CostCentersNestedListQuery) => ({
        total: tableData.costCenters.structuredCostCentersConnection.totalCount ?? 0,
      }),
      totalItemSummary: (totalCount: number) =>
        i18n.tp('finance.cost_centers_list.count_nested_cost_centers', { count: totalCount }),
    }),
    [
      company,
      mapData,
      fetchCostCenterDetails,
      hideFilters,
      canManageCostCenters,
      configurationJobCatalogStructure,
      handleEditAsset,
    ]
  )

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [company])

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  sorting: 'name',
                  width: 400,
                  label: i18n.t('finance.cost_centers_list.headers.name'),
                  render: (item: CostCenterNestedListDetails) => {
                    return {
                      type: 'text',
                      value: item.name,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.code'),
                  render: (item: CostCenterNestedListDetails) => {
                    if (item.structure !== null)
                      return {
                        type: 'text',
                        value: {
                          placeholder: i18n.t(
                            'finance.cost_centers_list.headers.job_catalog.not_applicable'
                          ),
                        },
                      }

                    return {
                      type: 'text',
                      value: item.code || '-',
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.legal_entity'),
                  render: (item: CostCenterNestedListDetails) => {
                    if (item.structure !== null)
                      return {
                        type: 'text',
                        value: {
                          placeholder: i18n.t(
                            'finance.cost_centers_list.headers.job_catalog.not_applicable'
                          ),
                        },
                      }

                    return {
                      type: 'text',
                      value:
                        item.legalEntity?.legalName ||
                        i18n.t('finance.cost_centers_list.legal_entity_unassigned'),
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.status'),
                  width: 200,
                  render: (item: CostCenterNestedListDetails) => {
                    if (item.structure !== null)
                      return {
                        type: 'text',
                        value: {
                          placeholder: i18n.t(
                            'finance.cost_centers_list.headers.job_catalog.not_applicable'
                          ),
                        },
                      }

                    return {
                      type: 'status',
                      value: {
                        status: getCostCenterStatus(item.status ?? COST_CENTER_STATUS.active),
                        label: getCostCenterStatusLabel(item.status ?? COST_CENTER_STATUS.active),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      {costCenterToEdit && (
        <CostCenterEditModal
          onClose={() => setCostCenterToEdit(null)}
          onSuccess={() => {
            setCostCenterToEdit(null)
          }}
          costCenter={costCenterToEdit}
        />
      )}
    </>
  )
}

export { CostCentersNestedList }
